import cx from "classnames";

import { Trash } from "components/icons/generated";
import SpacePath from "components/SpacePath";
import VendorConfigIcon from "components/VendorIcon";
import Link from "ds/components/Link";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import TextEllipsis from "ds/components/TextEllipsis";
import { SpaceAccessLevel, StackDependencyDetail } from "types/generated";
import { AccessResult } from "utils/access";

import DependencyItemName from "./Name";
import { OutputReferences } from "./OutputReferences";
import styles from "./styles.module.css";

type DependencyItemProps = {
  item: StackDependencyDetail;
  onDelete?: () => void;
  canManage: AccessResult;
  className?: string;
  onOutputReferencesEdit?: () => void;
  referenceCount?: number;
  graphMode?: boolean;
  isFocused?: boolean;
  isOutputReferencesDisallowed?: boolean;
  disallowedVendorName?: string;
  triggerAlways?: boolean;
};

const DependencyItem = ({
  item,
  onDelete,
  className,
  onOutputReferencesEdit,
  referenceCount,
  graphMode,
  isFocused,
  canManage,
  isOutputReferencesDisallowed,
  disallowedVendorName,
  triggerAlways,
}: DependencyItemProps) => {
  return (
    <Box direction="column" className={cx(styles.item, { [styles.focused]: isFocused }, className)}>
      <Box direction="row" align="center" justify="between" gap="medium" padding="large" fullWidth>
        <Box
          className={styles.stackInfoWrapper}
          direction="column"
          align="start"
          justify="center"
          shrink="1"
          gap="medium"
          fullWidth
        >
          <Box direction="row" align="center" justify="start" fullWidth>
            <VendorConfigIcon vendorConfig={item.vendorConfig} className={styles.vendorIcon} />

            <TextEllipsis tooltip={item.name} tooltipWidthMode="maxWidthSm">
              {(props) =>
                item.space.accessLevel === SpaceAccessLevel.None ? (
                  <DependencyItemName
                    {...props}
                    name={item.name}
                    className={cx(styles.itemName, props.className)}
                  />
                ) : (
                  <Link
                    {...props}
                    href={`/stack/${item.id}/dependencies`}
                    variant="secondary"
                    noEllipsis
                    className={cx(styles.itemNameLink, props.className)}
                  >
                    <DependencyItemName name={item.name} className={styles.itemName} />
                  </Link>
                )
              }
            </TextEllipsis>
          </Box>

          {!graphMode && (
            <Box direction="row" align="center" gap="small">
              <OutputReferences
                onEdit={onOutputReferencesEdit}
                referenceCount={referenceCount}
                disallowed={isOutputReferencesDisallowed}
                vendorName={disallowedVendorName}
                canManage={canManage}
              />
              {triggerAlways && <Badge variant="blue" type="weak" text="Always triggers" />}
            </Box>
          )}
        </Box>

        {onDelete && (canManage.result || canManage.message) && (
          <ButtonIcon
            disabled={!canManage.result}
            icon={Trash}
            onPress={onDelete}
            variant="ghostDanger"
          >
            {canManage.message ? `Delete dependency. ${canManage.message}` : "Delete dependency"}
          </ButtonIcon>
        )}
      </Box>

      <Box direction="row" className={styles.secondRow} align="center" justify="between">
        <SpacePath spaceId={item.space.id} fallbackName={item.space.name} />
      </Box>
    </Box>
  );
};

export default DependencyItem;
